import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, Check, X, MapPin, Inbox, Flag, Star, EyeOff, RotateCcw } from "lucide-react";

export default function NetworkAdmin() {
  const [requests, setRequests] = useState([]);
  const [reported, setReported] = useState([]);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/network/requests").then((r) => setRequests(r.data))
      .catch((e) => { if (e.response?.status === 403) setDenied(true); setRequests([]); })
      .finally(() => setLoading(false));
    api.get("/reviews/reported").then((r) => setReported(r.data)).catch(() => setReported([]));
  };
  useEffect(load, []);

  const decide = async (id, decision) => {
    await api.put(`/network/requests/${id}`, { decision });
    toast.success(decision === "approve" ? "Adhésion validée" : "Demande refusée");
    load();
  };

  const moderate = async (id, action) => {
    await api.put(`/reviews/${id}/moderate`, { action });
    toast.success(action === "hide" ? "Avis masqué" : action === "restore" ? "Avis rétabli" : "Signalement ignoré");
    load();
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-navy flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-sky-400" /></div>
        <div>
          <h1 className="text-3xl font-display font-bold text-navy">Admin Réseau STS</h1>
          <p className="text-slate-500">Validez les demandes d'adhésion au réseau Sellerie Tout Simplement</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="requests-panel">
        <h3 className="font-display font-semibold text-navy text-lg">Demandes en attente ({requests.length})</h3>
        {denied ? (
          <div className="text-center py-12 text-slate-500" data-testid="admin-denied"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />Accès réservé à l'administrateur du réseau.</div>
        ) : loading ? (
          <p className="text-sm text-slate-400 py-8 text-center">Chargement…</p>
        ) : requests.length === 0 ? (
          <div className="text-center py-12 text-slate-400" data-testid="no-requests"><Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />Aucune demande en attente.</div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between py-4 gap-3" data-testid={`request-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-navy">{r.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">{r.city && <><MapPin className="w-3 h-3" /> {r.city}</>}</div>
                  {r.specialties?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {r.specialties.slice(0, 4).map((s) => <span key={s} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px]">{s}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => decide(r.id, "approve")} data-testid={`approve-${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600"><Check className="w-4 h-4" /> Valider</button>
                  <button onClick={() => decide(r.id, "reject")} data-testid={`reject-${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-semibold hover:border-rose-300 hover:text-rose-500"><X className="w-4 h-4" /> Refuser</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="reported-panel">
        <h3 className="font-display font-semibold text-navy text-lg inline-flex items-center gap-2"><Flag className="w-5 h-5 text-amber-500" /> Avis signalés ({reported.length})</h3>
        <p className="text-sm text-slate-500 mt-1">Masquez un avis abusif : il n'impacte alors plus la note de l'atelier.</p>
        {reported.length === 0 ? (
          <div className="text-center py-10 text-slate-400" data-testid="no-reported">Aucun avis signalé.</div>
        ) : (
          <div className="mt-4 divide-y divide-slate-100">
            {reported.map((r) => (
              <div key={r.id} className="py-4" data-testid={`reported-${r.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-navy">{r.client_name} <span className="text-slate-400 font-normal">· {r.company_name}</span></div>
                    <div className="flex items-center gap-1 text-xs text-amber-500 mt-0.5"><Star className="w-3.5 h-3.5 fill-amber-400" /> {r.global}/5 {r.status === "hidden" && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-semibold">Masqué</span>}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status === "hidden"
                      ? <button onClick={() => moderate(r.id, "restore")} data-testid={`restore-${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-semibold hover:border-emerald-300 hover:text-emerald-600"><RotateCcw className="w-4 h-4" /> Rétablir</button>
                      : <button onClick={() => moderate(r.id, "hide")} data-testid={`hide-${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-rose-500 text-white text-sm font-semibold hover:bg-rose-600"><EyeOff className="w-4 h-4" /> Masquer</button>}
                    <button onClick={() => moderate(r.id, "dismiss")} data-testid={`dismiss-${r.id}`} className="inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-white border border-slate-200 text-slate-600 text-sm font-semibold hover:border-slate-300"><Check className="w-4 h-4" /> Ignorer</button>
                  </div>
                </div>
                {r.comment && <p className="text-sm text-slate-600 mt-2 italic">« {r.comment} »</p>}
                {r.report_reason && <p className="text-xs text-amber-600 mt-1">Motif : {r.report_reason}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
